import { APP_AVAILABLE } from "./app-availability";
import {
  CONTENT_REVIEWER_ID,
  activeContentReviewer,
  type ContentReviewer,
} from "./content-reviewer";

export const SITE_URL = "https://ishvaram.com";
export const SITE_NAME = "Ishvaram";
export const SITE_LOGO = `${SITE_URL}/cdn/shop/files/1.png?v=1723014680&width=340`;

// App store anchors are entity-graph SAME_AS links (rule 26) — only advertise
// them when the app is publicly installable, else they 404 and weaken trust.
const APP_STORE_SAME_AS = [
  "https://play.google.com/store/apps/details?id=com.ishvaram.app",
  "https://apps.apple.com/app/id6743459479",
];

export const SAME_AS: string[] = [
  ...(APP_AVAILABLE ? APP_STORE_SAME_AS : []),
  "https://ishvaram.com",
  "https://www.instagram.com/ishvaram/",
  "https://www.facebook.com/ishvaram.in",
  "https://www.youtube.com/channel/UCmLGFR2IpfqgoWaHuD-L9Vg",
  "https://www.wikidata.org/wiki/Q139596352",
];

export const ORG = {
  "@type": "Organization",
  "@id": `${SITE_URL}#org`,
  name: SITE_NAME,
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: SITE_LOGO,
  },
  ...(SAME_AS.length > 0 && { sameAs: SAME_AS }),
} as const;

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    ...ORG,
    description:
      "Vedic astrology tools powered by Swiss Ephemeris — free kundali, rashifal, dosha analysis, muhurat, and AI-guided Jyotish consultations.",
    areaServed: "IN",
    knowsLanguage: ["en", "hi", "sa"],
  };
}

export function mobileApplicationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "@id": `${SITE_URL}#app`,
    name: SITE_NAME,
    alternateName: "Ishvaram AI Vedic Astrology",
    operatingSystem: "ANDROID, IOS",
    applicationCategory: "LifestyleApplication",
    publisher: { "@id": `${SITE_URL}#org` },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    downloadUrl: [
      "https://play.google.com/store/apps/details?id=com.ishvaram.app",
      "https://apps.apple.com/app/id6743459479",
    ],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}#website`,
    url: SITE_URL,
    name: SITE_NAME,
    publisher: { "@id": `${SITE_URL}#org` },
    inLanguage: ["en", "hi"],
  };
}

export interface ArticleInput {
  headline: string;
  description: string;
  url?: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
  inLanguage?: "en" | "hi";
}

/** Article schema — for explanatory / editorial pages. */
export function articleSchema(input: ArticleInput) {
  const now = new Date().toISOString().split("T")[0];
  // E-E-A-T author/reviewer attribution (gap: eeat-named-reviewer-schema).
  // When a REAL credentialed reviewer is configured (content-reviewer.ts,
  // owner-fillable, disabled by default), YMYL-adjacent pages cite that named
  // Person as author + reviewer — the strongest 2026 E-E-A-T signal. Otherwise
  // we keep honest Organization-level attribution (never a fabricated human).
  const reviewer = activeContentReviewer();
  const authorRef = reviewer ? { "@id": CONTENT_REVIEWER_ID } : { "@id": `${SITE_URL}#org` };
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: input.headline,
    description: input.description,
    ...(input.url && { url: input.url, mainEntityOfPage: input.url }),
    ...(input.image && { image: input.image }),
    datePublished: input.datePublished ?? now,
    dateModified: input.dateModified ?? now,
    inLanguage: input.inLanguage ?? "en",
    author: authorRef,
    ...(reviewer && { reviewedBy: { "@id": CONTENT_REVIEWER_ID } }),
    publisher: { "@id": `${SITE_URL}#org` },
  };
}

export interface FAQItem {
  question: string;
  answer: string;
}

const SCHEMA_TEXT_ENTITIES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  hellip: "...",
  laquo: '"',
  ldquo: '"',
  lsaquo: "'",
  lsquo: "'",
  lt: "<",
  mdash: "-",
  ndash: "-",
  nbsp: " ",
  quot: '"',
  raquo: '"',
  rdquo: '"',
  rsaquo: "'",
  rsquo: "'",
};

function decodeSchemaTextEntities(value: string): string {
  return value.replace(/&(#\d+|#x[\da-f]+|[a-z][\da-z]+);/gi, (entity, rawName: string) => {
    const name = rawName.toLowerCase();
    if (name.startsWith("#x")) {
      const codePoint = Number.parseInt(name.slice(2), 16);
      return Number.isNaN(codePoint) || codePoint > 0x10ffff
        ? entity
        : String.fromCodePoint(codePoint);
    }
    if (name.startsWith("#")) {
      const codePoint = Number.parseInt(name.slice(1), 10);
      return Number.isNaN(codePoint) || codePoint > 0x10ffff
        ? entity
        : String.fromCodePoint(codePoint);
    }
    return SCHEMA_TEXT_ENTITIES[name] ?? entity;
  });
}

function plainSchemaText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = decodeSchemaTextEntities(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 0 ? text : null;
}

/**
 * FAQPage schema — the single highest-ROI AEO primitive.
 *
 * Rules:
 * - Q and A must be plain text (strip HTML from answer before passing in).
 * - Each answer should be a self-contained, quotable chunk (2–4 sentences).
 * - Every Q&A should contain the page's primary keyword variant so FAQ
 *   additions don't dilute topical focus.
 */
export function faqPageSchema(faqs: FAQItem[]) {
  const seenQuestions = new Set<string>();
  const mainEntity = faqs.flatMap((f) => {
    const question = plainSchemaText(f.question);
    const answer = plainSchemaText(f.answer);
    if (!question || !answer || seenQuestions.has(question)) return [];
    seenQuestions.add(question);
    return [
      {
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answer,
        },
      },
    ];
  });

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  };
}

export interface HowToStep {
  name: string;
  text: string;
  url?: string;
}

export interface HowToInput {
  name: string;
  description: string;
  steps: HowToStep[];
  totalTime?: string; // ISO 8601 duration, e.g. "PT2M"
  tool?: string[];
}

/** HowTo schema — for calculator pages ("How to check your Mangal Dosha"). */
export function howToSchema(input: HowToInput) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: input.name,
    description: input.description,
    ...(input.totalTime && { totalTime: input.totalTime }),
    ...(input.tool && {
      tool: input.tool.map((t) => ({ "@type": "HowToTool", name: t })),
    }),
    step: input.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      ...(s.url && { url: s.url }),
    })),
  };
}

export interface PersonInput {
  name: string;
  jobTitle: string;
  description: string;
  image?: string;
  url?: string;
  sameAs?: string[];
  knowsAbout?: string[];
  credentials?: string[];
}

/** Person schema — for astrologer profile pages. */
export function personSchema(input: PersonInput) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: input.name,
    jobTitle: input.jobTitle,
    description: input.description,
    ...(input.image && { image: input.image }),
    ...(input.url && { url: input.url }),
    ...(input.sameAs && input.sameAs.length > 0 && { sameAs: input.sameAs }),
    ...(input.knowsAbout && { knowsAbout: input.knowsAbout }),
    ...(input.credentials && {
      hasCredential: input.credentials.map((c) => ({
        "@type": "EducationalOccupationalCredential",
        name: c,
      })),
    }),
    worksFor: { "@id": `${SITE_URL}#org` },
  };
}

/**
 * Content-reviewer Person node — the named, credentialed human the Article
 * `author`/`reviewedBy` @ids point at (gap: eeat-named-reviewer-schema).
 * Returns `null` unless a REAL reviewer is configured + enabled
 * (content-reviewer.ts), so pages stay Organization-level until the owner fills
 * in a real person. When present, emit it into the page @graph (e.g. via
 * `schemaGraph(...)` / `pageSeoJsonLd`) so the author/reviewedBy reference
 * resolves to a full Person entity with credentials + sameAs.
 */
export function contentReviewerSchema(): Record<string, unknown> | null {
  const reviewer: ContentReviewer | null = activeContentReviewer();
  if (!reviewer) return null;
  return {
    ...personSchema({
      name: reviewer.name,
      jobTitle: reviewer.jobTitle,
      description: reviewer.description,
      image: reviewer.image,
      url: reviewer.url,
      sameAs: reviewer.sameAs,
      knowsAbout: reviewer.knowsAbout,
      credentials: reviewer.credentials,
    }),
    "@id": CONTENT_REVIEWER_ID,
  };
}

export interface BreadcrumbEntry {
  name: string;
  url: string;
}

export interface EventInput {
  name: string;
  description: string;
  url: string;
  startDate?: string;
  endDate?: string;
}

/** Event schema — for muhurat and festival planning pages. */
export function eventSchema(input: EventInput) {
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: input.name,
    description: input.description,
    url: input.url,
    ...(input.startDate && { startDate: input.startDate }),
    ...(input.endDate && { endDate: input.endDate }),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: "India",
      address: { "@type": "PostalAddress", addressCountry: "IN" },
    },
    organizer: { "@id": `${SITE_URL}#org` },
  };
}

/** BreadcrumbList schema — complements site navigation. */
export function breadcrumbSchema(trail: BreadcrumbEntry[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: b.url.startsWith("http") ? b.url : `${SITE_URL}${b.url}`,
    })),
  };
}

/** Collect multiple schemas into a graph — emit as a single JSON-LD block. */
export function schemaGraph(...schemas: Array<Record<string, unknown>>): Record<string, unknown> {
  const graph = schemas.map((s) => {
    const { ["@context"]: _ctx, ...rest } = s;
    return rest;
  });
  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
